import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {
  AdicionalComparacaoIfoodVM, ComparacaoAtualIfoodVM, ResolucaoAdicionalIfoodVM,
  SnapshotGrupoCanalVendaVM, SnapshotOpcaoCanalVendaVM
} from './canais-venda.models';

type ResumoGrupo = NonNullable<ComparacaoAtualIfoodVM['resumoAdicionais']>[number];
interface GrupoComparacao {
  local: SnapshotGrupoCanalVendaVM;
  remoto: SnapshotGrupoCanalVendaVM | null;
  resumo: ResumoGrupo;
  resolucao?: AdicionalComparacaoIfoodVM;
}
interface OpcaoComparacao {
  local: SnapshotOpcaoCanalVendaVM | null;
  remoto: SnapshotOpcaoCanalVendaVM | null;
  campos: ComparacaoAtualIfoodVM['diferencas'];
  diferente: boolean;
}

@Component({
  selector: 'app-comparacao-ifood',
  templateUrl: './comparacao-ifood.component.html',
  styleUrls: ['./comparacao-ifood.component.scss']
})
export class ComparacaoIfoodComponent implements OnChanges {
  @Input() dados: ComparacaoAtualIfoodVM;
  @Input() campos: {[campo: string]: boolean} = {};
  @Input() inclusoes: {[grupoId: number]: number[]} = {};
  @Input() resolucoes: {[grupoId: number]: ResolucaoAdicionalIfoodVM | null} = {};
  @Input() desabilitado = false;
  @Output() criacaoAtiva = new EventEmitter<number | null>();
  @Output() mapearCategoria = new EventEmitter<void>();
  grupos: GrupoComparacao[] = [];
  grupoId: number | null = null;
  filtro = 'TODOS';
  busca = '';
  imagensComErro = new Set<string>();
  escolhasInteragidas = new Set<number>();
  readonly lados: Array<'depois' | 'antes'> = ['depois', 'antes'];
  readonly ladosOpcoes: Array<'local' | 'remoto'> = ['local', 'remoto'];

  ngOnChanges(changes: SimpleChanges): void {
    if(!changes.dados || !this.dados) return;
    this.grupos = (this.dados.local?.estrutura.gruposAdicionais || []).map(local => ({local,
      remoto: this.dados.remoto?.estrutura.gruposAdicionais.find(it =>
        (local.alvoLocalId != null && it.alvoLocalId === local.alvoLocalId) || (!!local.idExterno && it.idExterno === local.idExterno)) || null,
      resumo: this.dados.resumoAdicionais?.find(it => it.grupoLocalId === local.alvoLocalId),
      resolucao: this.dados.adicionais?.find(it => it.local.alvoLocalId === local.alvoLocalId)}));
    this.escolhasInteragidas.clear();
    this.selecione(null);
  }

  selecione(id: number | null): void {
    this.grupoId = id;
    this.filtro = 'TODOS';
    this.busca = '';
    this.informeCriacao();
  }

  private informeCriacao(): void {
    const grupo = this.grupos.find(it => it.local.alvoLocalId === this.grupoId);
    this.criacaoAtiva.emit(grupo?.resumo?.podeCriarGrupo && !!grupo.local.opcoes.length && !this.bloqueioInclusao(this.grupoId) &&
      (this.resolucoes[this.grupoId]?.acao === 'NOVO' || !grupo.resolucao?.candidatos.length) ? this.grupoId : null);
  }

  bloqueioInclusao(grupoId: number | null): string | null {
    return this.dados?.resumoAdicionais?.find(it => it.grupoLocalId === grupoId)?.bloqueioInclusao ||
      this.dados?.avisoProduto || null;
  }

  get camposProduto(): ComparacaoAtualIfoodVM['diferencas'] {
    const rotulos = {nome: 'Nome', descricao: 'Descrição', imagem: 'Foto', preco: 'Preço', categoriaExternaId: 'Categoria'};
    return Object.keys(rotulos).filter(campo => !this.dados.avisoProduto || !['preco', 'categoriaExternaId'].includes(campo))
      .map(campo => {
        const diferenca = this.dados.diferencas.find(it => it.campo === campo);
        const valor = (lado: 'local' | 'remoto') => campo === 'preco' ? this.dados[lado]?.comercial
          : this.dados[lado]?.conteudo[campo];
        return diferenca || {campo, rotulo: rotulos[campo], antes: valor('remoto'), depois: valor('local'),
          nomeAntes: campo === 'categoriaExternaId' ? this.dados.categorias?.remoto : null,
          nomeDepois: campo === 'categoriaExternaId' ? this.dados.categorias?.local : null,
          bloqueio: campo === 'categoriaExternaId' ? this.dados.avisoCategoria : null};
      }).filter(it => this.filtro !== 'DIFERENCAS' || !!it.bloqueio || this.temDiferenca(it.campo));
  }

  temDiferenca(campo: string): boolean { return this.dados.diferencas.some(it => it.campo === campo); }

  chaveCampo(_indice: number, campo: ComparacaoAtualIfoodVM['diferencas'][number]): string {
    return campo.campo;
  }

  chaveOpcao(_indice: number, linha: OpcaoComparacao): string {
    return linha.local ? `local:${linha.local.alvoLocalId}` : `ifood:${linha.remoto.idExterno || linha.remoto.externalCode}`;
  }

  linhasOpcoes(grupo: GrupoComparacao, filtrar = true): OpcaoComparacao[] {
    const corresponde = (a: SnapshotOpcaoCanalVendaVM, b: SnapshotOpcaoCanalVendaVM) =>
      (a.alvoLocalId != null && a.alvoLocalId === b.alvoLocalId) || (!!a.idExterno && a.idExterno === b.idExterno);
    const linhas: OpcaoComparacao[] = grupo.local.opcoes.map(local => {
      const remoto = grupo.remoto?.opcoes.find(it => corresponde(local, it)) || null;
      const campos = this.dados.diferencas.filter(it => it.campo.startsWith(`opcao:${local.alvoLocalId}:`));
      return {local, remoto, campos, diferente: !remoto || !!campos.length ||
        ['nome', 'preco', 'disponivel'].some(campo => local[campo] !== remoto[campo])};
    });
    for(const remoto of grupo.remoto?.opcoes || [])
      if(!grupo.local.opcoes.some(it => corresponde(it, remoto))) linhas.push({local: null, remoto, campos: [], diferente: true});
    if(!filtrar) return linhas;
    const busca = this.busca.trim().toLocaleLowerCase('pt-BR');
    return linhas.filter(it => (!busca || `${it.local?.nome || ''} ${it.remoto?.nome || ''}`.toLocaleLowerCase('pt-BR').includes(busca)) &&
      (this.filtro !== 'DIFERENCAS' || it.diferente) && (this.filtro !== 'FALTANTES' || (!!it.local && !it.remoto)));
  }

  altereInclusao(grupo: SnapshotGrupoCanalVendaVM, opcaoId: number | null, selecionado: boolean): void {
    if(this.desabilitado || this.bloqueioInclusao(grupo.alvoLocalId)) return;
    const atuais = this.inclusoes[grupo.alvoLocalId] || [];
    const ids = opcaoId == null ? grupo.opcoes.map(it => it.alvoLocalId) : [opcaoId];
    const novos = selecionado ? Array.from(new Set([...atuais, ...ids])) : atuais.filter(id => !ids.includes(id));
    if(novos.length) this.inclusoes[grupo.alvoLocalId] = novos;
    else delete this.inclusoes[grupo.alvoLocalId];
    this.campos['adicionaisFaltantes'] = Object.keys(this.inclusoes).length > 0;
  }

  resolva(grupo: GrupoComparacao, decisao: ResolucaoAdicionalIfoodVM | null): void {
    this.resolucoes[grupo.local.alvoLocalId] = decisao;
    if(this.escolhasInteragidas.has(grupo.local.alvoLocalId)) {
      this.altereInclusao(grupo.local, null, !!decisao);
    }
    if(grupo.local.alvoLocalId === this.grupoId) this.informeCriacao();
  }

  urlImagem(url: string | null | undefined): string | null {
    return url && !this.imagensComErro.has(url) ? url : null;
  }
}
